import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';

import { useScreenShake, SCENE } from '../../lib/battle3d';
import Funfair from '../arena/Funfair';
import CcTable from '../arena/CcTable';
import Card3D from '../arena/Card3D';
import GameIntro from '../arena/GameIntro';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/cc`;

const CC_CARDS = {
  1:  { icon:'💸', name:'-100€',   color:'#ef4444', bg:'linear-gradient(145deg,#7f1d1d,#991b1b)' },
  2:  { icon:'💸', name:'-200€',   color:'#f97316', bg:'linear-gradient(145deg,#7c2d12,#9a3412)' },
  3:  { icon:'💸', name:'-300€',   color:'#dc2626', bg:'linear-gradient(145deg,#450a0a,#7f1d1d)' },
  4:  { icon:'😐', name:'Niente',  color:'#6b7280', bg:'linear-gradient(145deg,#1f2937,#374151)' },
  5:  { icon:'😐', name:'Niente',  color:'#6b7280', bg:'linear-gradient(145deg,#1f2937,#374151)' },
  6:  { icon:'😐', name:'Niente',  color:'#6b7280', bg:'linear-gradient(145deg,#1f2937,#374151)' },
  7:  { icon:'🪙', name:'Moneta',  color:'#eab308', bg:'linear-gradient(145deg,#713f12,#92400e)' },
  8:  { icon:'🎯', name:'Bersaglio',color:'#a855f7',bg:'linear-gradient(145deg,#4a1d96,#5b21b6)' },
  9:  { icon:'🎱', name:'Caduta',  color:'#06b6d4', bg:'linear-gradient(145deg,#0c4a6e,#0e7490)' },
  10: { icon:'🎡', name:'Ruota',   color:'#ec4899', bg:'linear-gradient(145deg,#831843,#9d174d)' },
};

const CC_ICONS = { 1:'💸', 2:'💸', 3:'💸', 4:'😐', 5:'😐', 6:'😐', 7:'🪙', 8:'🎯', 9:'🎱', 10:'🎡' };

const CC_BACK = { deep:'#08041a', mid:'#241063', rim:'#7c3aed', gold:'#d8b4fe',
  glow:'rgba(124,58,237,0.45)', emblem:'🃏' };
const CC_EDGE = ['#ede9fe', '#8b6fd0', '#33205e'];
const CC_SHAKE = { 1:0.7, 2:0.9, 3:1.2, 4:0, 5:0, 6:0, 7:0.6, 8:0.7, 9:0.8, 10:1.0 };

// ─── Helper: who plays next ──────────────────────────────────────────────────
// Returns players in turn order starting from picker, active only
const getTurnOrder = (miniGame, players) => {
  try {
    if (!miniGame || !players?.length) return [];
    const active = players.filter(p => !p.eliminated);
    if (!active.length) return [];
    const pickerIdx = active.findIndex(p => String(p.user_id) === String(miniGame.picker_id));
    const i = pickerIdx >= 0 ? pickerIdx : 0;
    return [...active.slice(i), ...active.slice(0, i)];
  } catch(e) { return []; }
};

const getNextChooser = (miniGame, players) => {
  try {
    const ordered = getTurnOrder(miniGame, players);
    const choices = miniGame?.choices || {};
    return ordered.find(p => {
      return choices[String(p.user_id)] === undefined && choices[p.user_id] === undefined;
    }) || null;
  } catch(e) { return null; }
};

// ═══════════════════════════════════════════════════════════════════════════
// MINI-GAMES — ogni componente è completamente autonomo
// Riceve miniGame (frozen, non aggiornato dal polling durante l'animazione)
// onAction invia la scelta al backend
// ═══════════════════════════════════════════════════════════════════════════

// ─── Helpers ────────────────────────────────────────────────────────────────
const useAnimationGuard = () => {
  const started = useRef(false);
  const start = (fn) => { if (!started.current) { started.current = true; fn(); } };
  return start;
};

const PlayerTag = ({ player, user, status, detail, color }) => (
  <div style={{ padding:'5px 10px', borderRadius:8, textAlign:'center', minWidth:70,
    background:`${color}15`, border:`1.5px solid ${color}`,
    fontSize:11 }}>
    <div style={{ fontWeight:800, color, marginBottom:2 }}>
      {player.user_id === user.user_id ? 'Tu' : player.username}
    </div>
    {status && <div style={{ fontSize:13 }}>{status}</div>}
    {detail && <div style={{ fontSize:10, color:'rgba(255,255,255,0.6)', marginTop:2 }}>{detail}</div>}
  </div>
);

// ─── Sound Engine ─────────────────────────────────────────────────────────────
const CC_SFX = {
  _ctx: null,
  getCtx() {
    if (!this._ctx) this._ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (this._ctx.state === 'suspended') this._ctx.resume();
    return this._ctx;
  },
  play(type) {
    try {
      const ctx = this.getCtx();
      const plays = {
        coin_flip: () => {
          // Spinning whoosh
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(200, ctx.currentTime);
          osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.08);
          osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.16);
          gain.gain.setValueAtTime(0.2, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
          osc.start(); osc.stop(ctx.currentTime + 0.2);
        },
        coin_land: () => {
          // Metallic clang
          [400, 600, 900].forEach((f, i) => {
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.type = 'triangle'; o.frequency.value = f;
            g.gain.setValueAtTime(0.3, ctx.currentTime + i*0.02);
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i*0.02 + 0.6);
            o.start(ctx.currentTime + i*0.02); o.stop(ctx.currentTime + i*0.02 + 0.6);
          });
        },
        coin_win: () => {
          [523, 659, 784, 1047].forEach((f,i) => {
            const o = ctx.createOscillator(); const g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.type = 'sine'; o.frequency.value = f;
            g.gain.setValueAtTime(0, ctx.currentTime+i*0.1);
            g.gain.linearRampToValueAtTime(0.25, ctx.currentTime+i*0.1+0.05);
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime+i*0.1+0.4);
            o.start(ctx.currentTime+i*0.1); o.stop(ctx.currentTime+i*0.1+0.4);
          });
        },
        coin_lose: () => {
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.connect(g); g.connect(ctx.destination);
          o.type = 'sawtooth';
          o.frequency.setValueAtTime(300, ctx.currentTime);
          o.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.5);
          g.gain.setValueAtTime(0.3, ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
          o.start(); o.stop(ctx.currentTime + 0.5);
        },
        dart_fly: () => {
          const buf = ctx.createBuffer(1, ctx.sampleRate*0.15, ctx.sampleRate);
          const d = buf.getChannelData(0);
          for (let i=0;i<d.length;i++) d[i]=(Math.random()*2-1)*(1-i/d.length)*0.5;
          const s = ctx.createBufferSource(); s.buffer = buf;
          const f = ctx.createBiquadFilter(); f.type='bandpass'; f.frequency.value=2000;
          s.connect(f); f.connect(ctx.destination); s.start();
        },
        dart_hit: () => {
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.connect(g); g.connect(ctx.destination);
          o.type = 'square'; o.frequency.value = 120;
          g.gain.setValueAtTime(0.4, ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime+0.3);
          o.start(); o.stop(ctx.currentTime+0.3);
        },
        reveal: () => {
          [800,1000,1200].forEach((f,i)=>{
            const o=ctx.createOscillator(),g=ctx.createGain();
            o.connect(g);g.connect(ctx.destination);
            o.type='sine';o.frequency.value=f;
            g.gain.setValueAtTime(0,ctx.currentTime+i*0.06);
            g.gain.linearRampToValueAtTime(0.2,ctx.currentTime+i*0.06+0.03);
            g.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+i*0.06+0.3);
            o.start(ctx.currentTime+i*0.06);o.stop(ctx.currentTime+i*0.06+0.3);
          });
        },
        ball_bounce: () => {
          const o=ctx.createOscillator(),g=ctx.createGain();
          o.connect(g);g.connect(ctx.destination);
          o.type='sine';
          o.frequency.setValueAtTime(300,ctx.currentTime);
          o.frequency.exponentialRampToValueAtTime(150,ctx.currentTime+0.1);
          g.gain.setValueAtTime(0.3,ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+0.15);
          o.start();o.stop(ctx.currentTime+0.15);
        },
        ball_land: () => {
          [200,400,300].forEach((f,i)=>{
            const o=ctx.createOscillator(),g=ctx.createGain();
            o.connect(g);g.connect(ctx.destination);
            o.type='triangle';o.frequency.value=f;
            g.gain.setValueAtTime(0.3,ctx.currentTime+i*0.04);
            g.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+i*0.04+0.4);
            o.start(ctx.currentTime+i*0.04);o.stop(ctx.currentTime+i*0.04+0.4);
          });
        },
        wheel_spin: () => {
          const o=ctx.createOscillator(),g=ctx.createGain();
          o.connect(g);g.connect(ctx.destination);
          o.type='sawtooth';
          o.frequency.setValueAtTime(80,ctx.currentTime);
          o.frequency.linearRampToValueAtTime(400,ctx.currentTime+1);
          o.frequency.linearRampToValueAtTime(80,ctx.currentTime+4);
          g.gain.setValueAtTime(0.15,ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+4.5);
          o.start();o.stop(ctx.currentTime+4.5);
        },
        wheel_stop: () => {
          [300,450,600,800].forEach((f,i)=>{
            const o=ctx.createOscillator(),g=ctx.createGain();
            o.connect(g);g.connect(ctx.destination);
            o.type='sine';o.frequency.value=f;
            g.gain.setValueAtTime(0,ctx.currentTime+i*0.08);
            g.gain.linearRampToValueAtTime(0.25,ctx.currentTime+i*0.08+0.05);
            g.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+i*0.08+0.5);
            o.start(ctx.currentTime+i*0.08);o.stop(ctx.currentTime+i*0.08+0.5);
          });
        },
        win_big: () => {
          [261,329,392,523,659,784].forEach((f,i)=>{
            const o=ctx.createOscillator(),g=ctx.createGain();
            o.connect(g);g.connect(ctx.destination);
            o.type='sine';o.frequency.value=f;
            g.gain.setValueAtTime(0,ctx.currentTime+i*0.07);
            g.gain.linearRampToValueAtTime(0.2,ctx.currentTime+i*0.07+0.05);
            g.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+i*0.07+0.5);
            o.start(ctx.currentTime+i*0.07);o.stop(ctx.currentTime+i*0.07+0.5);
          });
        },
      };
      plays[type]?.();
    } catch(e) {}
  }
};

// ─── COIN FLIP ─────────────────────────────────────────────────────────────────
const CoinFlip = ({ miniGame, user, players, onAction, mobile }) => {
  const active = players.filter(p => !p.eliminated);
  const choices = miniGame.choices || {};
  const done = miniGame.done;
  const result = miniGame.result;
  const ordered = getTurnOrder(miniGame, active);
  const nextChooser = getNextChooser(miniGame, active);
  const isMyTurn = !done && nextChooser?.user_id === user.user_id;
  const iChose = choices[String(user.user_id)] !== undefined || choices[user.user_id] !== undefined;
  const sentRef = useRef(iChose);
  const [localChose, setLocalChose] = useState(iChose);
  const [animPhase, setAnimPhase] = useState('idle');
  const [coinAngle, setCoinAngle] = useState(0);
  const [particles, setParticles] = useState([]);
  const guardStart = useAnimationGuard();

  useEffect(() => {
    if (done && result) {
      guardStart(() => {
        setAnimPhase('spinning');
        const speeds = [50,50,60,70,90,120,160,200,250,300];
        let flip = 0;
        const doFlip = () => {
          CC_SFX.play('coin_flip');
          setCoinAngle(a => a + 180);
          flip++;
          if (flip < speeds.length) setTimeout(doFlip, speeds[flip]);
          else {
            CC_SFX.play('coin_land');
            const finalAngle = result === 'testa' ? 0 : 180;
            setCoinAngle(a => a + ((finalAngle - a%360 + 360) % 360));
            setTimeout(() => {
              setAnimPhase('done');
              CC_SFX.play(result === 'testa' ? 'coin_win' : 'coin_lose');
              // Particles
              setParticles(Array.from({length:20},(_,i)=>({
                id:i, x:Math.random()*100, angle:Math.random()*360,
                color:result==='testa'?['#ffd700','#fbbf24','#fff'][i%3]:['#ef4444','#dc2626','#f87171'][i%3]
              })));
              setTimeout(()=>setParticles([]),2000);
            }, 600);
          }
        };
        setTimeout(doFlip, speeds[0]);
      });
    }
  }, [done, result]); // eslint-disable-line

  const choose = async (side) => {
    if (!isMyTurn || iChose || localChose || sentRef.current) return;
    sentRef.current = true; setLocalChose(true);
    await onAction({ type:'choose', side });
  };

  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:14,padding:mobile?12:20,position:'relative'}}>
      {/* Particles */}
      {particles.map(p=>(
        <motion.div key={p.id}
          initial={{x:'50%',y:'40%',opacity:1,scale:1}}
          animate={{x:`${p.x}%`,y:`${Math.random()*80}%`,opacity:0,scale:0,rotate:p.angle}}
          transition={{duration:1.5+Math.random(),ease:'easeOut'}}
          style={{position:'absolute',width:8,height:8,borderRadius:'50%',
            background:p.color,pointerEvents:'none',zIndex:20}}/>
      ))}

      <div style={{fontSize:mobile?18:24,fontWeight:900,color:'#eab308',
        fontFamily:"'Cinzel',serif",letterSpacing:'0.06em',
        textShadow:'0 0 20px rgba(234,179,8,0.6)'}}>
        🪙 LANCIO MONETA
      </div>

      {/* 3D Coin */}
      <div style={{perspective:600}}>
        <motion.div
          animate={animPhase==='done'?{scale:[1,1.15,1]}:{}}
          transition={{duration:0.4}}
          style={{
            width:mobile?110:150, height:mobile?110:150,
            position:'relative', transformStyle:'preserve-3d',
            transform:`rotateY(${coinAngle}deg)`,
            transition: animPhase==='spinning'?'transform 0.08s linear':'transform 0.5s ease-out',
          }}>
          {/* TESTA face */}
          <div style={{position:'absolute',inset:0,borderRadius:'50%',
            backfaceVisibility:'hidden',WebkitBackfaceVisibility:'hidden',
            background:'radial-gradient(circle at 35% 30%, #fff9c4, #fbbf24 40%, #d97706 70%, #92400e)',
            border:'6px solid #ca8a04',
            boxShadow:animPhase==='done'&&result==='testa'
              ?'0 0 50px rgba(234,179,8,1),0 0 100px rgba(234,179,8,0.5),inset 0 2px 8px rgba(255,255,255,0.5)'
              :'inset 0 2px 8px rgba(255,255,255,0.3),0 8px 30px rgba(0,0,0,0.5)',
            display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:4}}>
            <div style={{fontSize:mobile?36:50,filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.4))'}}>⭐</div>
            <div style={{fontSize:mobile?9:11,fontWeight:900,color:'#78350f',letterSpacing:'0.1em'}}>TESTA</div>
          </div>
          {/* CROCE face */}
          <div style={{position:'absolute',inset:0,borderRadius:'50%',
            backfaceVisibility:'hidden',WebkitBackfaceVisibility:'hidden',
            transform:'rotateY(180deg)',
            background:'radial-gradient(circle at 35% 30%, #f1f5f9, #94a3b8 40%, #475569 70%, #1e293b)',
            border:'6px solid #64748b',
            boxShadow:animPhase==='done'&&result==='croce'
              ?'0 0 50px rgba(239,68,68,0.9),0 0 100px rgba(239,68,68,0.4),inset 0 2px 8px rgba(255,255,255,0.3)'
              :'inset 0 2px 8px rgba(255,255,255,0.2),0 8px 30px rgba(0,0,0,0.5)',
            display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:4}}>
            <div style={{fontSize:mobile?36:50,filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'}}>💀</div>
            <div style={{fontSize:mobile?9:11,fontWeight:900,color:'#94a3b8',letterSpacing:'0.1em'}}>CROCE</div>
          </div>
        </motion.div>
      </div>

      {/* Status */}
      <motion.div key={animPhase}
        initial={{opacity:0,y:4}} animate={{opacity:1,y:0}}
        style={{fontSize:13,fontWeight:700,textAlign:'center',
          color:animPhase==='done'?(result==='testa'?'#fbbf24':'#ef4444'):'rgba(255,255,255,0.7)'}}>
        {!done && !iChose && isMyTurn && '✨ Scegli Testa o Croce!'}
        {!done && (iChose||localChose) && nextChooser && `⏳ Aspetta ${nextChooser.username}...`}
        {!done && (iChose||localChose) && !nextChooser && '✅ Tutti pronti!'}
        {!done && !isMyTurn && !iChose && nextChooser && `🎲 Turno di ${nextChooser?.username}...`}
        {done && animPhase==='spinning' && '🪙 La moneta vola...'}
        {done && animPhase==='done' && (result==='testa'?'⭐ TESTA! Raddoppio per chi ha indovinato!':'💀 CROCE! -200€ per chi ha sbagliato!')}
      </motion.div>

      {/* Player tags */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center'}}>
        {ordered.map(p=>{
          const chose = choices[String(p.user_id)] ?? choices[p.user_id];
          const won = animPhase==='done' && chose===result;
          const lost = animPhase==='done' && chose!==undefined && chose!==result;
          return (
            <motion.div key={p.user_id}
              animate={won?{scale:[1,1.1,1],boxShadow:['0 0 0px','0 0 20px #fbbf24','0 0 8px #fbbf24']}:
                        lost?{scale:[1,0.95,1]}:{}}
              transition={{duration:0.5}}
              style={{padding:'8px 12px',borderRadius:10,textAlign:'center',minWidth:72,
                background:won?'rgba(251,191,36,0.15)':lost?'rgba(239,68,68,0.15)':chose?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.04)',
                border:`1.5px solid ${won?'#fbbf24':lost?'#ef4444':chose?'rgba(255,255,255,0.2)':'rgba(255,255,255,0.08)'}`,
              }}>
              <div style={{fontSize:10,fontWeight:800,color:won?'#fbbf24':lost?'#ef4444':'rgba(255,255,255,0.6)',marginBottom:3}}>
                {p.user_id===user.user_id?'Tu':p.username}
              </div>
              <div style={{fontSize:20}}>{!chose?'❓':chose==='testa'?'⭐':'💀'}</div>
              {animPhase==='done'&&chose&&<div style={{fontSize:10,fontWeight:700,marginTop:2,color:won?'#fbbf24':'#ef4444'}}>{won?'×2 ✓':'-200€'}</div>}
            </motion.div>
          );
        })}
      </div>

      {/* Buttons */}
      {isMyTurn && !iChose && !localChose && (
        <div style={{display:'flex',gap:16}}>
          {[{s:'testa',i:'⭐',l:'TESTA',c:'#eab308',bg:'linear-gradient(135deg,#92400e,#b45309)'},
            {s:'croce',i:'💀',l:'CROCE',c:'#94a3b8',bg:'linear-gradient(135deg,#1e293b,#334155)'}].map(o=>(
            <motion.button key={o.s} onClick={()=>choose(o.s)}
              whileHover={{scale:1.08,y:-6,boxShadow:`0 12px 30px ${o.c}66`}}
              whileTap={{scale:0.94}}
              style={{width:mobile?90:110,height:mobile?90:110,
                background:o.bg, border:`3px solid ${o.c}`,
                borderRadius:20,cursor:'pointer',display:'flex',flexDirection:'column',
                alignItems:'center',justifyContent:'center',gap:6,
                boxShadow:`0 0 20px ${o.c}44,inset 0 1px 0 rgba(255,255,255,0.2)`}}>
              <span style={{fontSize:mobile?32:42}}>{o.i}</span>
              <span style={{fontSize:11,fontWeight:900,color:o.c,letterSpacing:'0.08em'}}>{o.l}</span>
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── TARGET ───────────────────────────────────────────────────────────────────
const TARGETS = [
  {emoji:'🏹',name:'Arciere',bg:'linear-gradient(135deg,#1e3a5f,#2563eb)',border:'#3b82f6',glow:'rgba(59,130,246,0.6)'},
  {emoji:'🦁',name:'Leone',  bg:'linear-gradient(135deg,#78350f,#d97706)',border:'#fbbf24',glow:'rgba(251,191,36,0.6)'},
  {emoji:'🐉',name:'Drago',  bg:'linear-gradient(135deg,#3b0764,#7c3aed)',border:'#a855f7',glow:'rgba(168,85,247,0.6)'},
  {emoji:'🔥',name:'Fuoco',  bg:'linear-gradient(135deg,#7f1d1d,#dc2626)',border:'#f87171',glow:'rgba(239,68,68,0.6)'},
];
const PRIZE_INFO = {
  'perdi_tutto':{label:'💀 Perdi tutto',short:'Perdi tutto',color:'#ef4444',icon:'💀'},
  'x2':{label:'×2 Raddoppio',short:'×2',color:'#22c55e',icon:'✨'},
  '+100':{label:'+100€',short:'+100€',color:'#3b82f6',icon:'💰'},
  '+300':{label:'+300€',short:'+300€',color:'#f59e0b',icon:'💎'},
};

const Target = ({ miniGame, user, players, onAction, mobile }) => {
  const active = players.filter(p => !p.eliminated);
  const choices = miniGame.choices || {};
  const done = miniGame.done;
  const figurePrizes = miniGame.figure_prizes;
  const ordered = getTurnOrder(miniGame, active);
  const nextChooser = getNextChooser(miniGame, active);
  const isMyTurn = !done && nextChooser?.user_id === user.user_id;
  const iChose = choices[String(user.user_id)] !== undefined || choices[user.user_id] !== undefined;
  const sentRef = useRef(iChose);
  const [localChose, setLocalChose] = useState(iChose);
  const animStarted = useRef(false);
  const [revealedFigs, setRevealedFigs] = useState([]);
  const [dartTarget, setDartTarget] = useState(null);
  const [animPhase, setAnimPhase] = useState('idle');
  const [dartTrail, setDartTrail] = useState([]);
  const guardStart = useAnimationGuard();

  useEffect(() => {
    if (done && figurePrizes) {
      guardStart(() => {
        setAnimPhase('throwing');
        let delay = 0;
        ordered.forEach((p, pi) => {
          const fig = parseInt(choices[String(p.user_id)] ?? choices[p.user_id] ?? 0);
          setTimeout(() => {
            CC_SFX.play('dart_fly');
            setDartTarget(fig);
            setTimeout(() => {
              CC_SFX.play('dart_hit');
              setRevealedFigs(prev => [...new Set([...prev, fig])]);
              setDartTarget(null);
              if (pi === ordered.length - 1) {
                setTimeout(() => {
                  setAnimPhase('done');
                  CC_SFX.play('win_big');
                }, 400);
              }
            }, 550);
          }, delay);
          delay += 1300;
        });
      });
    }
  }, [done, figurePrizes]); // eslint-disable-line

  const choose = async (fig) => {
    if (!isMyTurn || iChose || localChose || sentRef.current) return;
    sentRef.current = true; setLocalChose(true);
    await onAction({ type:'choose', figure:fig });
  };

  const FIG_POS = [
    {x:'22%',y:'25%'},{x:'72%',y:'25%'},
    {x:'22%',y:'65%'},{x:'72%',y:'65%'},
  ];

  const W = mobile ? 210 : 280;
  const H = mobile ? 180 : 220;

  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:12,padding:mobile?10:18}}>
      <div style={{fontSize:mobile?18:24,fontWeight:900,color:'#a855f7',
        fontFamily:"'Cinzel',serif",letterSpacing:'0.06em',
        textShadow:'0 0 20px rgba(168,85,247,0.6)'}}>
        🎯 TIRO A BERSAGLIO
      </div>

      {/* Board */}
      <div style={{position:'relative',width:W,height:H,
        background:'radial-gradient(ellipse at center, #0f0a1f 0%, #050210 100%)',
        border:'2px solid rgba(168,85,247,0.4)',borderRadius:20,overflow:'hidden',
        boxShadow:'0 0 30px rgba(168,85,247,0.2),inset 0 0 60px rgba(0,0,0,0.5)'}}>

        {/* Decorative rings */}
        {[92,70,48,26].map((s,i)=>(
          <div key={i} style={{position:'absolute',top:'50%',left:'50%',
            transform:'translate(-50%,-50%)',width:`${s}%`,height:`${s}%`,
            borderRadius:'50%',
            border:`1.5px solid rgba(168,85,247,${0.08+i*0.06})`,
            pointerEvents:'none'}}/>
        ))}
        {/* Center dot */}
        <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',
          width:10,height:10,borderRadius:'50%',background:'rgba(168,85,247,0.4)'}}/>

        {/* Targets */}
        {TARGETS.map((t, i) => {
          const fp = FIG_POS[i];
          const prize = figurePrizes?.[i];
          const pi = PRIZE_INFO[prize];
          const revealed = revealedFigs.includes(i);
          const isDartTarget = dartTarget === i;
          const myFig = parseInt(choices[String(user.user_id)] ?? choices[user.user_id]) === i;
          const whoChose = ordered.find(p => parseInt(choices[String(p.user_id)] ?? choices[p.user_id]) === i);
          const size = mobile ? 56 : 68;

          return (
            <motion.div key={i}
              animate={isDartTarget?{scale:[1,1.25,1.1],filter:['brightness(1)','brightness(2)','brightness(1.3)']}:
                       revealed?{scale:[1,1.1,1.05]}:{}}
              transition={{duration:0.4}}
              onClick={()=>choose(i)}
              style={{position:'absolute',left:fp.x,top:fp.y,
                transform:'translate(-50%,-50%)',
                width:size,height:size,borderRadius:14,
                background:revealed&&pi?pi.color+'33':t.bg,
                border:`2.5px solid ${revealed&&pi?pi.color:isDartTarget?'#fff':myFig?t.border:whoChose?t.border+'88':'rgba(255,255,255,0.15)'}`,
                display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
                cursor:isMyTurn&&!iChose&&!done?'pointer':'default',
                boxShadow:isDartTarget?`0 0 30px ${t.glow},0 0 60px ${t.glow}`:
                          revealed&&pi?`0 0 20px ${pi.color}66`:
                          myFig?`0 0 16px ${t.glow}`:'none',
                gap:2,overflow:'hidden',transition:'box-shadow 0.3s'}}>
              {/* Gloss */}
              <div style={{position:'absolute',top:0,left:0,right:0,height:'40%',
                background:'linear-gradient(to bottom,rgba(255,255,255,0.15),transparent)',pointerEvents:'none'}}/>
              {!revealed?(
                <>
                  <span style={{fontSize:mobile?22:30,filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'}}>{t.emoji}</span>
                  <span style={{fontSize:mobile?8:10,fontWeight:700,color:'rgba(255,255,255,0.7)'}}>{t.name}</span>
                  {myFig&&<span style={{position:'absolute',top:3,right:3,fontSize:10}}>🎯</span>}
                  {whoChose&&!myFig&&<span style={{position:'absolute',top:3,right:3,fontSize:8,color:'rgba(255,255,255,0.5)'}}>✓</span>}
                </>
              ):(
                <>
                  <span style={{fontSize:mobile?22:28}}>{pi?.icon||'?'}</span>
                  <span style={{fontSize:mobile?8:10,fontWeight:900,color:pi?.color||'#fff'}}>{pi?.short||'?'}</span>
                </>
              )}
            </motion.div>
          );
        })}

        {/* Dart in flight */}
        <AnimatePresence>
          {dartTarget !== null && (
            <motion.div
              initial={{left:'-10%',top:FIG_POS[dartTarget].y,opacity:0,scale:0.5}}
              animate={{left:`calc(${FIG_POS[dartTarget].x} - 18px)`,opacity:1,scale:1}}
              exit={{opacity:0,scale:0.3}}
              transition={{duration:0.3,ease:'easeOut'}}
              style={{position:'absolute',transform:'translateY(-50%)',fontSize:24,zIndex:20,
                filter:'drop-shadow(0 0 8px rgba(255,255,255,0.8))'}}>
              🎯
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Status */}
      <div style={{fontSize:12,fontWeight:700,textAlign:'center',color:'rgba(255,255,255,0.7)'}}>
        {!done&&isMyTurn&&!iChose&&!localChose&&'🎯 Scegli il tuo bersaglio!'}
        {!done&&(iChose||localChose)&&nextChooser&&`⏳ Aspetta ${nextChooser.username}...`}
        {!done&&(iChose||localChose)&&!nextChooser&&'✅ Tutti pronti!'}
        {!done&&!isMyTurn&&!iChose&&nextChooser&&`🎯 Turno di ${nextChooser.username}...`}
        {done&&animPhase==='throwing'&&'🎯 I dardi volano!'}
        {done&&animPhase==='done'&&'✅ Bersagli rivelati!'}
      </div>

      {/* Who chose */}
      <div style={{display:'flex',gap:6,flexWrap:'wrap',justifyContent:'center'}}>
        {ordered.map(p=>{
          const fig = choices[String(p.user_id)]!==undefined?parseInt(choices[String(p.user_id)]):
                      choices[p.user_id]!==undefined?parseInt(choices[p.user_id]):undefined;
          const prize = animPhase==='done'&&fig!==undefined&&figurePrizes?figurePrizes[fig]:null;
          const pi = prize?PRIZE_INFO[prize]:null;
          const t = fig!==undefined?TARGETS[fig]:null;
          return (
            <div key={p.user_id} style={{padding:'6px 10px',borderRadius:8,textAlign:'center',
              background:pi?`${pi.color}15`:t?`${t.glow}`.replace('0.6','0.1'):'rgba(255,255,255,0.04)',
              border:`1.5px solid ${pi?pi.color:t?t.border:'rgba(255,255,255,0.1)'}`,fontSize:11}}>
              <div style={{fontWeight:800,color:pi?pi.color:t?t.border:'rgba(255,255,255,0.4)',marginBottom:2}}>
                {p.user_id===user.user_id?'Tu':p.username}
              </div>
              <div>{t?t.emoji:'❓'}</div>
              {pi&&<div style={{fontSize:10,fontWeight:700,color:pi.color,marginTop:2}}>{pi.short}</div>}
            </div>
          );
        })}
      </div>

      {/* Buttons */}
      {isMyTurn&&!iChose&&!localChose&&(
        <div style={{display:'flex',gap:10,flexWrap:'wrap',justifyContent:'center'}}>
          {TARGETS.map((t,i)=>(
            <motion.button key={i} onClick={()=>choose(i)}
              whileHover={{scale:1.12,y:-6,boxShadow:`0 12px 30px ${t.glow}`}}
              whileTap={{scale:0.93}}
              style={{width:mobile?64:78,height:mobile?72:88,
                background:t.bg,border:`2.5px solid ${t.border}`,
                borderRadius:14,cursor:'pointer',display:'flex',flexDirection:'column',
                alignItems:'center',justifyContent:'center',gap:4,
                boxShadow:`0 0 16px ${t.glow}44,inset 0 1px 0 rgba(255,255,255,0.15)`}}>
              <span style={{fontSize:mobile?24:32}}>{t.emoji}</span>
              <span style={{fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.8)'}}>{t.name}</span>
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── PLINKO ───────────────────────────────────────────────────────────────────
const PLINKO_OPTS = [
  {key:'+100',label:'+100€',color:'#3b82f6',slots:3,prob:'3/6',icon:'💰'},
  {key:'+200',label:'+200€',color:'#22c55e',slots:2,prob:'2/6',icon:'💎'},
  {key:'x2',label:'×2',color:'#f59e0b',slots:1,prob:'1/6',icon:'🚀'},
];

const Plinko = ({ miniGame, user, players, onAction, mobile }) => {
  const active = players.filter(p => !p.eliminated);
  const choices = miniGame.choices || {};
  const done = miniGame.done;
  const slots = miniGame.slots || [];
  const landingIndex = miniGame.landing_index ?? null;
  const winningSlot = miniGame.winning_slot;
  const iChose = choices[String(user.user_id)]!==undefined||choices[user.user_id]!==undefined;
  const sentRef = useRef(iChose);
  const [localChose, setLocalChose] = useState(iChose);
  const [ballPos, setBallPos] = useState({x:50,y:-8});
  const [ballVisible, setBallVisible] = useState(false);
  const [animPhase, setAnimPhase] = useState('idle');
  const [lightSlot, setLightSlot] = useState(-1);
  const guardStart = useAnimationGuard();
  const totalChosen = Object.keys(choices).length;

  useEffect(() => {
    if (done && landingIndex !== null) {
      guardStart(() => {
        setBallVisible(true);
        setAnimPhase('falling');
        const targetX = 8 + landingIndex * 16.5;
        const path = [
          {x:50,y:0,d:0},
          {x:48+Math.random()*4-2,y:14,d:180},
          {x:targetX+(Math.random()*18-9),y:28,d:200},
          {x:targetX+(Math.random()*12-6),y:42,d:200},
          {x:targetX+(Math.random()*7-3.5),y:56,d:200},
          {x:targetX+(Math.random()*3-1.5),y:70,d:200},
          {x:targetX,y:83,d:220},
        ];
        let i=0;
        const step=()=>{
          if(i<path.length){
            setBallPos({x:path[i].x,y:path[i].y});
            if(i>0)CC_SFX.play('ball_bounce');
            i++;
            setTimeout(step,path[i-1].d);
          } else {
            CC_SFX.play('ball_land');
            setLightSlot(landingIndex);
            setTimeout(()=>{setAnimPhase('done');CC_SFX.play('win_big');},400);
          }
        };
        setTimeout(step,150);
      });
    }
  },[done,landingIndex]); // eslint-disable-line

  const choose = async(prize)=>{
    if(iChose||localChose||done||sentRef.current)return;
    sentRef.current=true;setLocalChose(true);
    await onAction({type:'choose',prize});
  };

  const W=mobile?220:280; const H=mobile?190:230;

  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:12,padding:mobile?10:18}}>
      <div style={{fontSize:mobile?18:24,fontWeight:900,color:'#06b6d4',
        fontFamily:"'Cinzel',serif",textShadow:'0 0 20px rgba(6,182,212,0.6)'}}>
        🎱 CADUTA LIBERA
      </div>

      {/* Board */}
      <div style={{position:'relative',width:W,height:H,
        background:'linear-gradient(180deg,#010d1a 0%,#020f1f 50%,#01080f 100%)',
        border:'2px solid rgba(6,182,212,0.35)',borderRadius:16,overflow:'hidden',
        boxShadow:'0 0 30px rgba(6,182,212,0.15),inset 0 0 40px rgba(0,0,0,0.6)'}}>

        {/* Pegs */}
        {Array.from({length:5},(_,r)=>{
          const n=r%2===0?5:4;
          return Array.from({length:n},(_,c)=>({
            x:r%2===0?(c/4)*82+9:(c/3)*82+18,
            y:(r/4)*68+8, key:`${r}-${c}`
          }));
        }).flat().map(p=>(
          <div key={p.key} style={{position:'absolute',left:`${p.x}%`,top:`${p.y}%`,
            transform:'translate(-50%,-50%)',
            width:mobile?8:10,height:mobile?8:10,borderRadius:'50%',
            background:'radial-gradient(circle at 35% 35%,rgba(6,182,212,1),rgba(6,182,212,0.5))',
            boxShadow:'0 0 8px rgba(6,182,212,0.7)'}}/>
        ))}

        {/* Ball */}
        {ballVisible&&(
          <motion.div
            animate={{left:`${ballPos.x}%`,top:`${ballPos.y}%`}}
            transition={{duration:0.18,ease:'easeInOut'}}
            style={{position:'absolute',transform:'translate(-50%,-50%)',
              width:mobile?20:24,height:mobile?20:24,borderRadius:'50%',zIndex:10,
              background:'radial-gradient(circle at 35% 30%,#fff,#06b6d4 50%,#0e7490)',
              boxShadow:'0 0 20px rgba(6,182,212,1),0 0 40px rgba(6,182,212,0.5)',
              border:'2px solid rgba(255,255,255,0.5)'}}/>
        )}

        {/* Slot dividers */}
        {[1,2,3,4,5].map(i=>(
          <div key={i} style={{position:'absolute',bottom:0,left:`${i*16.67}%`,
            width:2,height:'22%',background:'rgba(6,182,212,0.3)',borderRadius:'2px 2px 0 0'}}/>
        ))}

        {/* Slots */}
        {slots.map((slot,i)=>{
          const opt=PLINKO_OPTS.find(o=>o.key===slot);
          const isLanding=i===landingIndex&&lightSlot===i;
          return (
            <motion.div key={i}
              animate={isLanding?{background:[`${opt?.color||'#06b6d4'}11`,`${opt?.color||'#06b6d4'}55`,`${opt?.color||'#06b6d4'}33`]}:{}}
              transition={{duration:0.5}}
              style={{position:'absolute',bottom:0,
                left:`${i*16.67}%`,width:'16.67%',height:'20%',
                background:isLanding?`${opt?.color||'#06b6d4'}33`:'rgba(255,255,255,0.03)',
                border:`1px solid ${isLanding?opt?.color:'transparent'}`,
                display:'flex',alignItems:'center',justifyContent:'center',
                boxShadow:isLanding?`0 0 20px ${opt?.color}88`:undefined}}>
              <span style={{fontSize:mobile?8:10,fontWeight:900,color:opt?.color||'#06b6d4',textAlign:'center',lineHeight:1.1}}>
                {slot}
              </span>
            </motion.div>
          );
        })}
      </div>

      {/* Status */}
      <div style={{fontSize:12,fontWeight:700,textAlign:'center',color:'rgba(255,255,255,0.7)'}}>
        {!done&&!iChose&&!localChose&&'🤫 Scegli in segreto! Gli altri non vedranno la tua scelta'}
        {!done&&(iChose||localChose)&&`✅ Scelto! (${totalChosen}/${active.length} pronti)`}
        {done&&animPhase==='falling'&&'🎱 La pallina cade...'}
        {done&&animPhase==='done'&&`🎯 Atterrata su: ${winningSlot}!`}
      </div>

      {/* Choice buttons */}
      {!localChose&&!iChose&&!done&&(
        <div style={{display:'flex',gap:12}}>
          {PLINKO_OPTS.map(o=>(
            <motion.button key={o.key} onClick={()=>choose(o.key)}
              whileHover={{scale:1.08,y:-6,boxShadow:`0 12px 30px ${o.color}55`}}
              whileTap={{scale:0.93}}
              style={{padding:mobile?'12px 16px':'14px 20px',
                background:`linear-gradient(135deg,${o.color}22,${o.color}11)`,
                border:`2.5px solid ${o.color}`,borderRadius:16,cursor:'pointer',
                display:'flex',flexDirection:'column',alignItems:'center',gap:4,
                boxShadow:`0 0 16px ${o.color}33`}}>
              <span style={{fontSize:24}}>{o.icon}</span>
              <span style={{fontSize:mobile?14:18,fontWeight:900,color:o.color}}>{o.label}</span>
              <span style={{fontSize:9,color:'rgba(255,255,255,0.4)',background:'rgba(255,255,255,0.06)',
                padding:'2px 8px',borderRadius:10}}>{o.prob} probabilità</span>
            </motion.button>
          ))}
        </div>
      )}

      {/* Results */}
      {animPhase==='done'&&(
        <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center'}}>
          {active.map(p=>{
            const chose=choices[String(p.user_id)]||choices[p.user_id];
            const won=chose===winningSlot;
            const opt=PLINKO_OPTS.find(o=>o.key===chose);
            return (
              <motion.div key={p.user_id}
                initial={{scale:0.8,opacity:0}}animate={{scale:1,opacity:1}}
                style={{padding:'8px 12px',borderRadius:10,textAlign:'center',
                  background:won?`${opt?.color}22`:'rgba(239,68,68,0.15)',
                  border:`2px solid ${won?opt?.color:'#ef4444'}`,fontSize:12}}>
                <div style={{fontWeight:800,color:won?opt?.color:'#ef4444',marginBottom:3}}>
                  {p.user_id===user.user_id?'Tu':p.username}
                </div>
                <div style={{fontSize:18}}>{won?opt?.icon:'😞'}</div>
                <div style={{fontSize:11,fontWeight:700,color:won?opt?.color:'#ef4444',marginTop:2}}>
                  {won?opt?.label:'-100€'}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── MEGA WHEEL ────────────────────────────────────────────────────────────────
const DIRS_CFG=[
  {key:'N',label:'Nord', emoji:'⬆️',color:'#3b82f6',deg:270,pos:{top:'2%',left:'50%',transform:'translateX(-50%)'}},
  {key:'E',label:'Est',  emoji:'➡️',color:'#22c55e',deg:0,  pos:{right:'2%',top:'50%',transform:'translateY(-50%)'}},
  {key:'S',label:'Sud',  emoji:'⬇️',color:'#ef4444',deg:90, pos:{bottom:'2%',left:'50%',transform:'translateX(-50%)'}},
  {key:'W',label:'Ovest',emoji:'⬅️',color:'#f59e0b',deg:180,pos:{left:'2%',top:'50%',transform:'translateY(-50%)'}},
];
const SEG_C={'-100':'#ef4444','-200':'#f97316','-300':'#dc2626','0':'#4b5563','x2':'#22c55e','x3':'#8b5cf6','x5':'#ffd700','xtutto':'#0f172a'};
const SEG_L={'-100':'-100€','-200':'-200€','-300':'-300€','0':'Salvo!','x2':'×2','x3':'×3','x5':'×5','xtutto':'Perdi tutto'};
const WHEEL_DATA=[...Array(21).fill('-100'),...Array(13).fill('-200'),...Array(7).fill('-300'),...Array(4).fill('0'),...Array(4).fill('x2'),...Array(2).fill('x3'),...Array(2).fill('xtutto'),...Array(1).fill('x5')];

const MegaWheel = ({ miniGame, user, players, onAction, mobile }) => {
  const active = players.filter(p => !p.eliminated);
  const choices = miniGame.choices || {};
  const takenDirs = miniGame.taken_dirs || [];
  const done = miniGame.done;
  const dirResults = miniGame.dir_results || {};
  const stopIndex = miniGame.stop_index ?? 0;
  const ordered = getTurnOrder(miniGame, active);
  const nextChooser = getNextChooser(miniGame, active);
  const isMyTurn = !done && nextChooser?.user_id === user.user_id;
  const iChose = choices[String(user.user_id)]!==undefined||choices[user.user_id]!==undefined;
  const sentRef = useRef(iChose);
  const [localChose, setLocalChose] = useState(iChose);
  const [wheelDeg, setWheelDeg] = useState(0);
  const [animPhase, setAnimPhase] = useState('idle');
  const [tickCount, setTickCount] = useState(0);
  const guardStart = useAnimationGuard();

  useEffect(()=>{
    if(done&&Object.keys(dirResults).length>0){
      guardStart(()=>{
        setAnimPhase('spinning');
        CC_SFX.play('wheel_spin');
        const segAngle=360/54;
        const targetDeg=360*8-(stopIndex*segAngle)+180;
        setWheelDeg(targetDeg);
        // Tick sounds
        let ticks=0;
        const tickInterval=setInterval(()=>{
          CC_SFX.play('ball_bounce');
          ticks++;
          if(ticks>20)clearInterval(tickInterval);
        },200);
        setTimeout(()=>{
          clearInterval(tickInterval);
          CC_SFX.play('wheel_stop');
          setTimeout(()=>{
            setAnimPhase('done');
            CC_SFX.play('win_big');
          },500);
        },5000);
      });
    }
  },[done,dirResults]); // eslint-disable-line

  const choose=async(dir)=>{
    if(!isMyTurn||takenDirs.includes(dir)||iChose||localChose||sentRef.current)return;
    sentRef.current=true;setLocalChose(true);
    await onAction({type:'choose',direction:dir});
  };

  const W=mobile?200:260;
  const cx=W/2,cy=W/2,r=W/2-5;
  const segAngle=360/54;

  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:10,padding:mobile?10:16}}>
      <div style={{fontSize:mobile?17:23,fontWeight:900,color:'#ec4899',
        fontFamily:"'Cinzel',serif",textShadow:'0 0 20px rgba(236,72,153,0.6)'}}>
        🎡 MEGA RUOTA
      </div>

      {/* Wheel + direction badges */}
      <div style={{position:'relative',width:W+70,height:W+70,display:'flex',alignItems:'center',justifyContent:'center'}}>

        {/* Direction badges */}
        {DIRS_CFG.map(d=>{
          const myDir=choices[String(user.user_id)]===d.key||choices[user.user_id]===d.key;
          const whoChose=active.find(p=>choices[String(p.user_id)]===d.key||choices[p.user_id]===d.key);
          const res=animPhase==='done'?dirResults[d.key]:null;
          const positive=res&&!res.startsWith('-')&&res!=='xtutto';
          return (
            <div key={d.key} style={{position:'absolute',...d.pos,zIndex:15}}>
              <motion.div
                animate={animPhase==='done'&&myDir?
                  {scale:[1,1.3,1.1],boxShadow:['0 0 0px','0 0 30px '+d.color,'0 0 15px '+d.color]}:{}}
                transition={{duration:0.5}}
                style={{width:mobile?36:42,height:mobile?36:42,borderRadius:'50%',
                  background:myDir?d.color:whoChose?`${d.color}55`:'rgba(255,255,255,0.08)',
                  border:`2.5px solid ${whoChose||myDir?d.color:'rgba(255,255,255,0.15)'}`,
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:mobile?16:20,
                  boxShadow:myDir?`0 0 20px ${d.color}`:'none',
                  cursor:isMyTurn&&!takenDirs.includes(d.key)?'pointer':'default',
                  position:'relative'}}
                onClick={()=>choose(d.key)}>
                {d.emoji}
                {/* Result badge */}
                {res&&(
                  <motion.div initial={{scale:0,opacity:0}} animate={{scale:1,opacity:1}} transition={{delay:0.3}}
                    style={{position:'absolute',top:-20,left:'50%',transform:'translateX(-50%)',
                      background:SEG_C[res]||'#666',borderRadius:6,padding:'2px 6px',
                      fontSize:9,fontWeight:900,color:'#fff',whiteSpace:'nowrap',
                      boxShadow:`0 0 10px ${SEG_C[res]}99`,border:`1px solid ${SEG_C[res]}`}}>
                    {SEG_L[res]}
                  </motion.div>
                )}
                {/* Who chose label */}
                {whoChose&&animPhase!=='done'&&(
                  <div style={{position:'absolute',bottom:-16,left:'50%',transform:'translateX(-50%)',
                    fontSize:8,color:d.color,fontWeight:700,whiteSpace:'nowrap'}}>
                    {whoChose.user_id===user.user_id?'Tu':whoChose.username}
                  </div>
                )}
              </motion.div>
            </div>
          );
        })}

        {/* Pointer */}
        <div style={{position:'absolute',top:28,left:'50%',transform:'translateX(-50%)',
          width:0,height:0,borderLeft:'12px solid transparent',borderRight:'12px solid transparent',
          borderTop:'24px solid #ffd700',filter:'drop-shadow(0 0 8px rgba(255,215,0,0.9))',zIndex:20}}/>

        {/* SVG Wheel */}
        <div style={{
          transform:`rotate(${wheelDeg}deg)`,
          transition:animPhase==='spinning'?`transform 5s cubic-bezier(0.1,0,0.05,1)`:'none',
          borderRadius:'50%',
          boxShadow:'0 0 30px rgba(236,72,153,0.3),0 0 60px rgba(0,0,0,0.5)',
        }}>
          <svg width={W} height={W} viewBox={`0 0 ${W} ${W}`}>
            {WHEEL_DATA.map((seg,i)=>{
              const a1=(i*segAngle-90)*Math.PI/180;
              const a2=((i+1)*segAngle-90)*Math.PI/180;
              const x1=cx+r*Math.cos(a1),y1=cy+r*Math.sin(a1);
              const x2=cx+r*Math.cos(a2),y2=cy+r*Math.sin(a2);
              const mx=cx+(r*0.65)*Math.cos((a1+a2)/2);
              const my=cy+(r*0.65)*Math.sin((a1+a2)/2);
              return (
                <g key={i}>
                  <path d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 0,1 ${x2},${y2} Z`}
                    fill={SEG_C[seg]||'#666'} stroke="rgba(0,0,0,0.6)" strokeWidth={1}/>
                  {/* Segment label for larger segments */}
                  {(seg==='x2'||seg==='x3'||seg==='x5'||seg==='0')&&(
                    <text x={mx} y={my} textAnchor="middle" dominantBaseline="middle"
                      fill="rgba(255,255,255,0.9)" fontSize={mobile?6:8} fontWeight="bold">
                      {SEG_L[seg]}
                    </text>
                  )}
                </g>
              );
            })}
            {/* Outer ring */}
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={2}/>
            {/* Center hub */}
            <circle cx={cx} cy={cy} r={W/9} fill="#0a0618" stroke="rgba(255,255,255,0.3)" strokeWidth={3}/>
            <circle cx={cx} cy={cy} r={W/12} fill="rgba(236,72,153,0.3)" stroke="rgba(236,72,153,0.6)" strokeWidth={1.5}/>
            <text x={cx} y={cy+6} textAnchor="middle" fill="white" fontSize={W/8}>🎡</text>
          </svg>
        </div>
      </div>

      {/* Status */}
      <div style={{fontSize:12,fontWeight:700,color:'rgba(255,255,255,0.7)',textAlign:'center'}}>
        {!done&&isMyTurn&&(Object.keys(choices).length===0?'🎡 Sei il primo — scegli la tua direzione!':'🎡 Scegli la tua direzione!')}
        {!done&&!isMyTurn&&nextChooser&&`⏳ Turno di ${nextChooser.username}...`}
        {!done&&iChose&&!nextChooser&&'✅ Tutti pronti! La ruota girerà...'}
        {done&&animPhase==='spinning'&&'🎡 La ruota gira... BUONA FORTUNA!'}
        {done&&animPhase==='done'&&'✅ La ruota si è fermata!'}
      </div>

      {/* Direction buttons (if not inside wheel) */}
      {!done&&(
        <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center'}}>
          {DIRS_CFG.map(d=>{
            const taken=takenDirs.includes(d.key);
            const canPick=isMyTurn&&!taken;
            const myDir=choices[String(user.user_id)]===d.key||choices[user.user_id]===d.key;
            return (
              <motion.button key={d.key} onClick={()=>choose(d.key)} disabled={!canPick}
                whileHover={canPick?{scale:1.1,y:-5}:{}}
                whileTap={canPick?{scale:0.93}:{}}
                style={{padding:'8px 14px',
                  background:myDir?`${d.color}44`:taken?'rgba(255,255,255,0.03)':`${d.color}11`,
                  border:`2px solid ${taken&&!myDir?'rgba(255,255,255,0.07)':d.color}`,
                  borderRadius:12,cursor:canPick?'pointer':'not-allowed',
                  opacity:taken&&!myDir?0.3:1,
                  display:'flex',flexDirection:'column',alignItems:'center',gap:2,
                  boxShadow:myDir?`0 0 20px ${d.color}66`:'none',
                  transition:'all 0.2s'}}>
                <span style={{fontSize:mobile?18:22}}>{d.emoji}</span>
                <span style={{fontSize:9,fontWeight:700,color:taken&&!myDir?'rgba(255,255,255,0.2)':d.color}}>
                  {d.label}{taken&&!myDir?' ✓':''}
                </span>
              </motion.button>
            );
          })}
        </div>
      )}

      {/* Results summary */}
      {animPhase==='done'&&(
        <div style={{display:'flex',gap:6,flexWrap:'wrap',justifyContent:'center'}}>
          {active.map(p=>{
            const dir=choices[String(p.user_id)]||choices[p.user_id];
            const d=DIRS_CFG.find(x=>x.key===dir);
            const res=dirResults[dir];
            const positive=res&&!res.startsWith('-')&&res!=='xtutto';
            return (
              <motion.div key={p.user_id}
                initial={{scale:0.8,opacity:0}}animate={{scale:1,opacity:1}}
                style={{padding:'6px 10px',borderRadius:8,textAlign:'center',
                  background:positive?'rgba(34,197,94,0.15)':'rgba(239,68,68,0.15)',
                  border:`1.5px solid ${positive?'#22c55e':'#ef4444'}`,fontSize:11}}>
                <div style={{fontWeight:800,color:positive?'#22c55e':'#ef4444',marginBottom:2}}>
                  {p.user_id===user.user_id?'Tu':p.username}
                </div>
                <div>{d?.emoji} {dir}</div>
                <div style={{fontWeight:700,marginTop:2}}>{SEG_L[res]||'?'}</div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── CC Card: il corpo 3D condiviso con la faccia da luna park ───────────────
const CcCard = ({ card, index, onPick, canPick, mobile, isRevealing }) => {
  const [lastValue, setLastValue] = useState(card.value);
  useEffect(() => { if (card.value !== null) setLastValue(card.value); }, [card.value]);
  const displayValue = card.value ?? lastValue;
  const meta = CC_CARDS[displayValue];
  const revealed = card.revealed;

  const front = (
    <div style={{
      position:'absolute', inset:0, background: meta?.bg || '#1a1340',
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      gap: mobile?6:10, padding:8,
    }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:'40%',
        background:'linear-gradient(to bottom,rgba(255,255,255,0.15),transparent)' }} />
      <div style={{ position:'absolute', top: mobile?5:8, left: mobile?6:10,
        fontSize: mobile?7:9, fontWeight:700, color: meta?.color, fontFamily:"'Cinzel',serif" }}>{displayValue}</div>
      <div style={{ position:'absolute', bottom: mobile?5:8, right: mobile?6:10, transform:'rotate(180deg)',
        fontSize: mobile?7:9, fontWeight:700, color: meta?.color, fontFamily:"'Cinzel',serif" }}>{displayValue}</div>
      <div style={{ fontSize: mobile?30:50, lineHeight:1, zIndex:1,
        filter:`drop-shadow(0 0 14px ${meta?.color || '#a855f7'})` }}>{meta?.icon}</div>
      <div style={{ fontSize: mobile?8:11, fontWeight:900, color:'#fff', zIndex:1,
        fontFamily:"'Cinzel',serif", textAlign:'center', letterSpacing:'0.05em' }}>
        {meta?.name}
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity:0, y:-70, rotateZ: index % 2 ? 9 : -9 }}
      animate={{ opacity:1, y:0, rotateZ:0 }}
      transition={{ delay: 0.25 + index * 0.11, type:'spring', stiffness:150, damping:15 }}
      style={{ transformStyle:'preserve-3d' }}
    >
      <Card3D
        revealed={revealed}
        index={index}
        onPick={onPick}
        canPick={canPick}
        isRevealing={isRevealing}
        mobile={mobile}
        back={CC_BACK}
        edge={CC_EDGE}
        frontBase="#12082a"
        tone={meta?.color || '#a855f7'}
        front={front}
        prompt="SCEGLI"
      />
    </motion.div>
  );
};

// ─── Player Box ───────────────────────────────────────────────────────────────
const CcPlayerBox = ({ player, isActive, variant='full' }) => {
  const isMini = variant === 'compact';
  const bust = player.eliminated;
  const balColor = player.balance >= 1000 ? '#22c55e' : player.balance >= 500 ? '#eab308' : '#ef4444';

  if (isMini) {
    return (
      <div style={{position:'relative', borderRadius:12, overflow:'hidden', minWidth:82,
        background: isActive ? 'linear-gradient(160deg,#1a0d3a,#0f0828)' : 'linear-gradient(160deg,#12092a,#0a0620)',
        border:`1.5px solid ${isActive ? '#7c3aed' : 'rgba(124,58,237,0.25)'}`,
        boxShadow: isActive ? '0 0 0 2px rgba(124,58,237,0.3),0 0 20px rgba(124,58,237,0.2)' : '0 2px 10px rgba(0,0,0,0.4)',
        padding:'6px 8px'}}>
        <div style={{position:'absolute', top:0, left:0, right:0, height:'42%',
          background:'linear-gradient(to bottom,rgba(255,255,255,0.05),transparent)', pointerEvents:'none'}}/>
        <div style={{fontSize:7, fontWeight:700, color: bust?'#4b5563':'#c4b5fd',
          fontFamily:"'Cinzel',serif", textAlign:'center', overflow:'hidden', textOverflow:'ellipsis',
          whiteSpace:'nowrap', marginBottom:5}}>
          {player.username}
        </div>
        <div style={{height:1, background:'linear-gradient(to right,transparent,rgba(124,58,237,0.3),transparent)', marginBottom:5}}/>
        {/* Balance col */}
        <div style={{display:'flex', gap:4, justifyContent:'center'}}>
          <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:2}}>
            <span style={{fontSize:14}}>💰</span>
            <div style={{minWidth:36, height:18, background:'linear-gradient(180deg,#0d0a1f,#1a1340)',
              border:'1px solid rgba(124,58,237,0.4)', borderRadius:4,
              display:'flex', alignItems:'center', justifyContent:'center'}}>
              <span style={{fontSize:8, fontWeight:900, color:balColor, fontFamily:"'Cinzel',serif"}}>
                {player.balance}
              </span>
            </div>
          </div>
          <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:2}}>
            <span style={{fontSize:14}}>🃏</span>
            <div style={{minWidth:20, height:18, background:'linear-gradient(180deg,#0d0a1f,#1a1340)',
              border:'1px solid rgba(124,58,237,0.4)', borderRadius:4,
              display:'flex', alignItems:'center', justifyContent:'center'}}>
              <span style={{fontSize:9, fontWeight:900, color:'#c4b5fd', fontFamily:"'Cinzel',serif"}}>
                {player.jolly}
              </span>
            </div>
          </div>
        </div>
        {isActive && !bust && (
          <motion.div animate={{opacity:[0.4,1,0.4], scale:[0.8,1.3,0.8]}} transition={{duration:0.85, repeat:Infinity}}
            style={{position:'absolute', top:5, right:5, width:6, height:6, borderRadius:'50%',
              background:'#7c3aed', boxShadow:'0 0 8px #7c3aed'}}/>
        )}
        {isActive && !bust && (
          <motion.div animate={{opacity:[0.15,0.6,0.15]}} transition={{duration:0.85, repeat:Infinity}}
            style={{position:'absolute', inset:0, borderRadius:12, border:'2px solid #7c3aed', pointerEvents:'none'}}/>
        )}
        {bust && (
          <div style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.8)', borderRadius:12,
            display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:2}}>
            {[...Array(5)].map((_,i)=><div key={i} style={{position:'absolute', top:0, bottom:0,
              left:`${15+i*17}%`, width:1.5, background:'rgba(124,58,237,0.2)', borderRadius:1}}/>)}
            <span style={{fontSize:14, position:'relative', zIndex:1}}>💀</span>
            <span style={{fontSize:7, fontWeight:900, color:'#4b5563', position:'relative', zIndex:1}}>OUT</span>
          </div>
        )}
      </div>
    );
  }

  // Full variant
  return (
    <div style={{position:'relative', borderRadius:14, overflow:'hidden', minWidth:220,
      background: isActive ? 'linear-gradient(160deg,#1a0d3a,#0f0828)' : 'linear-gradient(160deg,#12092a,#0a0620)',
      border:`2px solid ${isActive ? '#7c3aed' : 'rgba(124,58,237,0.3)'}`,
      boxShadow: isActive
        ? '0 0 0 3px rgba(124,58,237,0.25),0 8px 28px rgba(124,58,237,0.2),0 2px 8px rgba(0,0,0,0.6)'
        : '0 4px 20px rgba(0,0,0,0.5)',
      padding:'12px 16px'}}>
      {/* Gloss */}
      <div style={{position:'absolute', top:0, left:0, right:0, height:'40%',
        background:'linear-gradient(to bottom,rgba(255,255,255,0.06),transparent)', pointerEvents:'none'}}/>
      {/* Top bar */}
      <div style={{height:3, background: isActive
        ? 'linear-gradient(to right,#7c3aed,#a855f7,#ec4899)'
        : 'linear-gradient(to right,rgba(124,58,237,0.4),rgba(168,85,247,0.3))',
        marginBottom:10, borderRadius:2}}/>
      {/* Name */}
      <div style={{fontSize:12, fontWeight:800, color: bust?'#4b5563':'#c4b5fd',
        fontFamily:"'Cinzel',serif", textAlign:'center', overflow:'hidden',
        textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:10}}>
        {player.username}
      </div>
      {/* Balance */}
      <div style={{textAlign:'center', marginBottom:8}}>
        <motion.span key={player.balance}
          initial={{scale:1.3, color:'#fff'}} animate={{scale:1, color:balColor}}
          transition={{duration:0.4}}
          style={{fontSize:22, fontWeight:900, fontFamily:"'Cinzel',serif",
            textShadow:`0 0 12px ${balColor}88`}}>
          {player.balance}€
        </motion.span>
      </div>
      {/* Progress bar to 2000 */}
      <div style={{height:4, borderRadius:2, background:'rgba(255,255,255,0.08)', overflow:'hidden', marginBottom:8}}>
        <motion.div animate={{width:`${Math.min(100,(player.balance/2000)*100)}%`}}
          transition={{type:'spring', stiffness:200, damping:22}}
          style={{height:'100%', borderRadius:2,
            background:`linear-gradient(to right,${balColor}88,${balColor})`,
            boxShadow:`0 0 8px ${balColor}88`}}/>
      </div>
      {/* Jolly */}
      <div style={{display:'flex', justifyContent:'center', gap:6}}>
        {[0,1].map(i => (
          <span key={i} style={{fontSize:16, opacity:i<player.jolly?1:0.12,
            filter:i<player.jolly?'drop-shadow(0 1px 3px rgba(139,92,246,0.5))':'none'}}>🃏</span>
        ))}
      </div>
      {/* TURN badge */}
      {isActive && !bust && (
        <motion.div animate={{opacity:[0.7,1,0.7]}} transition={{duration:0.9, repeat:Infinity}}
          style={{position:'absolute', top:8, right:8, background:'#7c3aed', borderRadius:6,
            padding:'2px 8px', fontSize:8, fontWeight:900, color:'#fff', letterSpacing:'0.1em'}}>
          TURN
        </motion.div>
      )}
      {isActive && !bust && (
        <motion.div animate={{opacity:[0.2,0.6,0.2]}} transition={{duration:0.9, repeat:Infinity}}
          style={{position:'absolute', inset:0, borderRadius:14, border:'3px solid #7c3aed', pointerEvents:'none'}}/>
      )}
      {bust && (
        <div style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.82)', borderRadius:14,
          display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:4}}>
          <div style={{position:'absolute', inset:0, display:'flex', justifyContent:'space-evenly', padding:'0 10px'}}>
            {[...Array(8)].map((_,i)=><div key={i} style={{width:1.5, background:'rgba(124,58,237,0.2)', borderRadius:1}}/>)}
          </div>
          <span style={{fontSize:28, position:'relative', zIndex:1}}>💀</span>
          <span style={{fontSize:10, fontWeight:900, color:'#4b5563', position:'relative', zIndex:1, letterSpacing:'0.1em'}}>ELIMINATO</span>
        </div>
      )}
    </div>
  );
};

// ─── Main CcGame ──────────────────────────────────────────────────────────────
const CcGame = ({ user }) => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [revealingCard, setRevealingCard] = useState(null);
  const [mobile, setMobile] = useState(window.innerWidth < 640);
  const [showIntro, setShowIntro] = useState(() => sessionStorage.getItem(`cc_intro_${gameId}`) !== 'done');
  const { shakeControls, shake } = useScreenShake();
  const [frozenMiniGame, setFrozenMiniGame] = useState(null); // frozen for animation
  const [miniGameKey, setMiniGameKey] = useState(0);
  const miniGameSent = useRef(false); // prevent double-send
  const pollingRef = useRef(null);

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 640);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const updateFrozenMiniGame = useCallback((nextMG, prevMG) => {
    if (!prevMG && nextMG) {
      // New mini-game: fresh mount
      setFrozenMiniGame(nextMG);
      setMiniGameKey(k => k + 1);
      miniGameSent.current = false;
    } else if (prevMG && nextMG && !prevMG.done && !nextMG.done) {
      // Update choices only — use functional update to merge
      setFrozenMiniGame(prev => ({...prev, choices: nextMG.choices, taken_dirs: nextMG.taken_dirs}));
    } else if (prevMG && nextMG && !prevMG.done && nextMG.done) {
      // Mini-game resolved — update with results for animation
      setFrozenMiniGame(nextMG);
    } else if (prevMG && !nextMG) {
      // Cleared by backend
      miniGameSent.current = false;
      setTimeout(() => setFrozenMiniGame(null), 1500);
    }
  }, []);

  const loadGame = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/game/${gameId}`);
      setGameState(prev => {
        const incoming = res.data;
        updateFrozenMiniGame(incoming?.mini_game, prev?.mini_game);
        return incoming;
      });
      setLoading(false);
    } catch { toast.error('Errore caricamento'); }
  }, [gameId, updateFrozenMiniGame]);

  useEffect(() => {
    loadGame();
    pollingRef.current = setInterval(loadGame, 2500);
    const ch = supabase.channel(`cc_${gameId}`)
      .on('postgres_changes', {event:'UPDATE', schema:'public', table:'cc_game_sessions', filter:`id=eq.${gameId}`},
        (p) => {
          const incoming = p.new;
          setGameState(prev => {
            updateFrozenMiniGame(incoming?.mini_game, prev?.mini_game);
            return incoming;
          });
        }).subscribe();
    return () => { clearInterval(pollingRef.current); ch.unsubscribe(); supabase.removeChannel(ch); };
  }, [loadGame, gameId]);

  const handlePickCard = async (cardIndex) => {
    if (actionInProgress) return;
    setActionInProgress(true);
    setRevealingCard(cardIndex);
    try {
      const res = await axios.post(`${API}/game/pick-card`, {game_id:gameId, user_id:user.user_id, card_index:cardIndex});
      setFeedback(res.data.message);
      const power = CC_SHAKE[res.data.card_value] ?? 0;
      if (power) setTimeout(() => shake(power), 700);
      setTimeout(() => { setFeedback(null); setRevealingCard(null); }, 2500);
      await loadGame();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
      setRevealingCard(null);
    } finally { setActionInProgress(false); }
  };

  const handleUseJolly = async () => {
    if (actionInProgress) return;
    setActionInProgress(true);
    try {
      await axios.post(`${API}/game/use-jolly`, {game_id:gameId, user_id:user.user_id});
      setFeedback('Turno saltato con il Jolly!');
      setTimeout(() => setFeedback(null), 2500);
      await loadGame();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setActionInProgress(false); }
  };

  const handleMiniGameAction = async (action) => {
    if (miniGameSent.current) return; // already sent
    miniGameSent.current = true;
    try {
      await axios.post(`${API}/game/mini-game-action`, {game_id:gameId, user_id:user.user_id, action});
      await loadGame();
    } catch (e) {
      miniGameSent.current = false; // allow retry on error
      const msg = e.response?.data?.detail || 'Errore';
      if (msg !== 'Nessun mini-gioco attivo') toast.error(msg);
    }
  };

  // miniGameChoicesKey not needed - loadGame handles all frozenMiniGame updates

  // Stable shuffle for card table
  const tableDisplayIcons = useMemo(() => {
    const onTable = gameState?.cards_on_table || [];
    const vals = onTable.filter(v => v !== null);
    for (let i = vals.length-1; i>0; i--) {
      const j = Math.floor(Math.random()*(i+1));
      [vals[i],vals[j]] = [vals[j],vals[i]];
    }
    let vi = 0;
    return onTable.map(v => v === null ? null : vals[vi++]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(gameState?.cards_on_table||[]).join(',')]);

  if (loading || !gameState) return (
    <div style={{minHeight:'100dvh', background:'#0a0618', display:'flex', alignItems:'center', justifyContent:'center'}}>
      <motion.div animate={{opacity:[0.4,1,0.4]}} transition={{duration:1.5, repeat:Infinity}}
        style={{color:'#a855f7', fontSize:18, fontWeight:700, fontFamily:"'Cinzel',serif"}}>
        Caricamento... 🎡
      </motion.div>
    </div>
  );

  const players = gameState.players || [];
  const currentPlayer = players[gameState.current_turn];
  const myPlayer = players.find(p => p.user_id === user.user_id);
  const isMyTurn = currentPlayer?.user_id === user.user_id;
  const canAct = isMyTurn && !myPlayer?.eliminated && !actionInProgress && !gameState.mini_game;
  const cards = (gameState.cards_on_table||[]).map((v,i) => ({
    value: v, revealed: v===null || revealingCard===i
  }));
  const miniGame = gameState.mini_game;
  const myIndex = players.findIndex(p => p.user_id === user.user_id);
  const pos = {};
  players.forEach((p,i) => {
    pos[['bottom','right','top','left'][(i-myIndex+players.length)%players.length]] = p;
  });

  // Win screen
  if (gameState.status === 'finished') {
    const winner = players.find(p => p.user_id === gameState.winner);
    const isMe = winner?.user_id === user.user_id;
    return (
      <div style={{minHeight:'100dvh', background:'linear-gradient(135deg,#0a0618,#130926)',
        display:'flex', alignItems:'center', justifyContent:'center', padding:20}}>
        <motion.div initial={{scale:0.2,opacity:0,y:60}} animate={{scale:1,opacity:1,y:0}}
          transition={{type:'spring', stiffness:180, damping:18}}
          style={{background:'linear-gradient(160deg,#1a0d3a,#0f0828)', borderRadius:28,
            padding:'48px clamp(24px,6vw,56px)',
            border:`2px solid ${isMe?'#7c3aed':'rgba(124,58,237,0.3)'}`,
            boxShadow:isMe?'0 0 60px rgba(124,58,237,0.3),0 20px 60px rgba(0,0,0,0.9)':'0 20px 60px rgba(0,0,0,0.9)',
            textAlign:'center', maxWidth:380, width:'100%'}}>
          <motion.div animate={{scale:isMe?[1,1.2,1]:1}} transition={{duration:1, repeat:Infinity}}
            style={{fontSize:72, marginBottom:16}}>{isMe?'💰':'💀'}</motion.div>
          <h1 style={{fontFamily:"'Cinzel',serif", fontSize:'clamp(22px,5vw,36px)', fontWeight:900,
            margin:'0 0 10px', color:isMe?'#a855f7':'#4b5563'}}>
            {isMe?'Hai Vinto!':(winner?.username||'Fine')}
          </h1>
          <p style={{color:'rgba(255,255,255,0.4)', fontSize:14, margin:'0 0 32px'}}>
            {isMe?`Saldo finale: ${myPlayer?.balance}€`:`${winner?.username} ha vinto la partita.`}
          </p>
          <motion.button onClick={() => navigate('/games')} whileHover={{scale:1.04}} whileTap={{scale:0.97}}
            style={{background:'linear-gradient(135deg,#7c3aed,#5b21b6)', border:'none', borderRadius:14,
              padding:'14px 36px', fontSize:14, fontWeight:900, color:'#fff', cursor:'pointer',
              fontFamily:"'Cinzel',serif", letterSpacing:'0.08em', textTransform:'uppercase',
              boxShadow:'0 4px 20px rgba(124,58,237,0.4)'}}>
            Torna ai Giochi
          </motion.button>
        </motion.div>
      </div>
    );
  }

  const BG = <Funfair mobile={mobile} />;

  // Dove siede ogni giocatore attorno al tavolo, nello spazio 3D
  const seat = (p) => (mobile
    ? { top:'translateZ(-70px)', left:'rotateY(14deg)', right:'rotateY(-14deg)', bottom:'translateZ(16px)' }
    : {
        top:'translateZ(-230px) rotateX(8deg)',
        left:'rotateY(21deg) translateZ(-30px)',
        right:'rotateY(-21deg) translateZ(-30px)',
        bottom:'translateZ(45px) rotateX(-5deg)',
      })[p];

  return (
    <div style={{width:'100dvw', height:'100dvh', position:'relative', overflow:'hidden'}}>
      {BG}

      {/* Mini-game overlay */}
      <AnimatePresence>
        {frozenMiniGame && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            style={{position:'fixed', inset:0, zIndex:50, background:'rgba(0,0,0,0.85)',
              backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:16}}>
            <motion.div initial={{scale:0.8,y:40}} animate={{scale:1,y:0}} exit={{scale:0.8,y:40}}
              style={{background:'linear-gradient(160deg,#1a0d3a,#0f0828)',
                border:'2px solid rgba(124,58,237,0.5)', borderRadius:24, padding:mobile?16:24,
                width:'100%', maxWidth:500, boxShadow:'0 0 60px rgba(124,58,237,0.2)'}}>
              {frozenMiniGame.type === 'coin_flip' && players.length > 0 && (
                <CoinFlip key={miniGameKey} miniGame={frozenMiniGame} user={user} players={players} onAction={handleMiniGameAction} mobile={mobile}/>
              )}
              {frozenMiniGame.type === 'target' && players.length > 0 && (
                <Target key={miniGameKey} miniGame={frozenMiniGame} user={user} players={players} onAction={handleMiniGameAction} mobile={mobile}/>
              )}
              {frozenMiniGame.type === 'plinko' && players.length > 0 && (
                <Plinko key={miniGameKey} miniGame={frozenMiniGame} user={user} players={players} onAction={handleMiniGameAction} mobile={mobile}/>
              )}
              {frozenMiniGame.type === 'wheel' && players.length > 0 && (
                <MegaWheel key={miniGameKey} miniGame={frozenMiniGame} user={user} players={players} onAction={handleMiniGameAction} mobile={mobile}/>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div animate={shakeControls} style={{
        position:'relative', zIndex:10, height:'100%',
        perspective: mobile ? SCENE.perspectiveMobile : SCENE.perspective,
        perspectiveOrigin:'50% 38%',
      }}>
      <div style={{height:'100%', display:'flex', flexDirection:'column', padding: mobile?'8px 8px':'44px 30px 20px', gap:6, transformStyle:'preserve-3d'}}>
        {/* TOP */}
        <div style={{display:'flex', justifyContent:'center', flexShrink:0, transformStyle:'preserve-3d'}}>
          {pos.top && (
            <div style={{transform:seat('top'), transformStyle:'preserve-3d'}}>
              <CcPlayerBox player={pos.top} isActive={currentPlayer?.user_id===pos.top.user_id} variant={mobile?'compact':'full'}/>
            </div>
          )}
        </div>

        {/* MIDDLE */}
        <div style={{flex:1, display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, minHeight:0, transformStyle:'preserve-3d'}}>
          <div style={{flexShrink:0, transform:seat('left'), transformStyle:'preserve-3d'}}>
            {pos.left && <CcPlayerBox player={pos.left} isActive={currentPlayer?.user_id===pos.left.user_id} variant={mobile?'compact':'full'}/>}
          </div>

          <div style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, minWidth:0}}>
            {/* Insegna: cosa resta sul tavolo */}
            <div style={{display:'inline-block', borderRadius: mobile?10:14, padding: mobile?'6px 16px':'9px 26px',
              background:'linear-gradient(165deg,rgba(28,12,62,0.96),rgba(8,3,20,0.98))',
              boxShadow:'0 0 0 1px rgba(168,85,247,0.45),0 6px 22px rgba(0,0,0,0.8),inset 0 1px 0 rgba(216,180,254,0.16),inset 0 -8px 18px rgba(0,0,0,0.6)'}}>
              <div style={{fontSize:mobile?6.5:8.5, fontWeight:800, letterSpacing:'0.24em',
                color:'rgba(216,180,254,0.55)', textTransform:'uppercase', textAlign:'center', marginBottom:mobile?2:4}}>
                ★ Sul Tavolo ★
              </div>
              <div style={{display:'flex', justifyContent:'center', alignItems:'center', gap:mobile?12:20}}>
                {tableDisplayIcons.map((v,i) => (
                  <motion.span key={i} animate={v===null?{}:{opacity:[0.6,1,0.6]}} transition={{duration:2, repeat:Infinity}}
                    style={{fontSize:mobile?22:32,
                      opacity: cards[i]?.revealed ? 0.2 : 1,
                      filter: cards[i]?.revealed ? 'grayscale(1)' : 'none',
                      transition:'all 0.4s'}}>
                    {v ? (CC_ICONS[v] || v) : '·'}
                  </motion.span>
                ))}
              </div>
            </div>

            {/* Il tavolo */}
            <CcTable mobile={mobile} active={canAct}>
              <div style={{display:'grid', gridTemplateColumns: mobile?'repeat(2,auto)':'repeat(4,auto)',
                columnGap: mobile?12:22, rowGap: mobile?16:26,
                justifyContent:'center', justifyItems:'center', transformStyle:'preserve-3d'}}>
                {cards.map((card,i) => (
                  <CcCard key={i} card={card} index={i} onPick={handlePickCard}
                    canPick={canAct && !card.revealed} mobile={mobile} isRevealing={revealingCard===i}/>
                ))}
              </div>
            </CcTable>

            {/* Feedback */}
            <div style={{minHeight:36, display:'flex', alignItems:'center', justifyContent:'center'}}>
              <AnimatePresence mode="wait">
                {feedback && (
                  <motion.div key="fb" initial={{opacity:0,y:8,scale:0.9}} animate={{opacity:1,y:0,scale:1}} exit={{opacity:0}}
                    style={{background:'rgba(26,13,58,0.95)', backdropFilter:'blur(8px)',
                      border:'1px solid rgba(124,58,237,0.4)', borderRadius:20, padding:'8px 24px',
                      color:'#c4b5fd', fontSize:mobile?12:14, fontWeight:700,
                      boxShadow:'0 4px 20px rgba(124,58,237,0.2)', textAlign:'center'}}>
                    {feedback}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Waiting */}
            {!isMyTurn && !myPlayer?.eliminated && !miniGame && (
              <div style={{background:'rgba(10,6,24,0.85)', backdropFilter:'blur(8px)',
                border:'1px solid rgba(124,58,237,0.15)', borderRadius:9999,
                padding:'8px 24px', color:'rgba(196,181,253,0.5)', fontSize:mobile?12:14, fontWeight:600}}>
                Turno di <span style={{color:'#a855f7', fontWeight:800}}>{currentPlayer?.username}</span>...
              </div>
            )}

            {/* Round badge */}
            <div style={{fontSize:10, color:'rgba(168,85,247,0.4)', fontWeight:700, letterSpacing:'0.1em'}}>
              ROUND {gameState.round || 1}
            </div>
          </div>

          <div style={{flexShrink:0, transform:seat('right'), transformStyle:'preserve-3d'}}>
            {pos.right && <CcPlayerBox player={pos.right} isActive={currentPlayer?.user_id===pos.right.user_id} variant={mobile?'compact':'full'}/>}
          </div>
        </div>

        {/* BOTTOM */}
        <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:8, flexShrink:0, transformStyle:'preserve-3d'}}>
          {myPlayer && myPlayer.jolly > 0 && canAct && (
            <motion.button onClick={handleUseJolly} disabled={actionInProgress}
              whileHover={{scale:1.05}} whileTap={{scale:0.95}}
              animate={{boxShadow:['0 0 8px rgba(124,58,237,0.3)','0 0 24px rgba(124,58,237,0.7)','0 0 8px rgba(124,58,237,0.3)']}}
              transition={{duration:1.6, repeat:Infinity}}
              style={{display:'flex', alignItems:'center', gap:8, padding:mobile?'7px 16px':'9px 22px',
                background:'linear-gradient(135deg,#4c1d95,#5b21b6)', border:'none', borderRadius:20,
                cursor:'pointer', boxShadow:'0 4px 16px rgba(124,58,237,0.4)'}}>
              <span style={{fontSize:mobile?14:18}}>🃏</span>
              <span style={{fontSize:mobile?11:13, fontWeight:800, color:'#e9d5ff', letterSpacing:'0.05em'}}>
                Salta il turno
              </span>
            </motion.button>
          )}
          {pos.bottom && (
            <div style={{transform:seat('bottom'), transformStyle:'preserve-3d'}}>
              <CcPlayerBox player={pos.bottom} isActive={currentPlayer?.user_id===pos.bottom.user_id} variant='full'/>
            </div>
          )}
        </div>
      </div>
      </motion.div>

      <AnimatePresence>
        {showIntro && (
          <GameIntro
            emblem="🎡"
            title="Crazy Cards"
            tagline="La fortuna gira"
            rule="Parti da 500€ — chi tiene la cassa vince"
            cry="SI GIOCA"
            players={players.map(p => p.username)}
            theme={{ accent:'#c084fc', accentSoft:'rgba(192,132,252,0.6)', text:'#f3e8ff', ring:'rgba(192,132,252,0.6)' }}
            onDone={() => { sessionStorage.setItem(`cc_intro_${gameId}`, 'done'); setShowIntro(false); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CcGame;